import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import { QueryKeys } from '~/services/queryClient'
import {
  ListUsageEventsParams,
  UsageEventItem,
  UsageEventsFacets,
  UsageEventsResponse,
  getSuperAdminUsageEvents,
  getSuperAdminUsageEventsFacets
} from '~/services/superAdminService'

// React Query default `staleTime` (0) — every mount and every queryKey change
// refetches. Refresh button calls `refetch()` which is unconditional. Super-admin
// numbers should reflect the DB right now, not 30s ago.

/**
 * Cursor-paginated list of usage_events. Backed by `useInfiniteQuery` so
 * "Load more" appends the next page rather than replacing the current
 * one. The first page sends no cursor; each subsequent page sends the
 * `next_cursor` returned by the previous one. When the API returns
 * `next_cursor: null` the hook's `hasNextPage` flips to false and the
 * UI hides the Load more affordance.
 */
export const useSuperAdminUsageEvents = (params: ListUsageEventsParams = {}) => {
  const {
    data,
    isPending,
    isError,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage
  } = useInfiniteQuery<UsageEventsResponse, Error>({
    queryKey: [...QueryKeys.AdminUsageEvents, params],
    queryFn: ({ pageParam }) =>
      getSuperAdminUsageEvents({
        ...params,
        cursor: (pageParam as string | undefined) ?? undefined
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => lastPage.next_cursor ?? undefined,
    meta: { persist: false }
  })

  // Flatten all loaded pages into a single ordered list. `useInfiniteQuery`
  // keeps page boundaries internally; the UI just wants one stream.
  const items: UsageEventItem[] = data?.pages.flatMap(p => p.items) ?? []
  return {
    items,
    isLoading: isPending,
    isError,
    error: error as Error | null,
    refetch,
    fetchNextPage,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage
  }
}

export const useSuperAdminUsageEventsFacets = (days: number = 30) => {
  const { data, isPending } = useQuery<UsageEventsFacets, Error>({
    queryKey: QueryKeys.AdminUsageEventsFacets(days),
    queryFn: () => getSuperAdminUsageEventsFacets(days),
    meta: { persist: false }
  })

  return {
    facets: data,
    isLoading: isPending
  }
}
